/**
 * iOS Safari opens Privy's login and signing popups as tabs. Privy closes the
 * popup from our page (`popup.close()`), and Safari then shows the tab next to
 * the popup instead of returning to Rarible. Here, on iOS, our page asks the
 * popup to close itself instead. A popup on our origin (the end of the login
 * flow) does so; any other popup is closed the old way after a short wait.
 */
export const POPUP_SELF_CLOSE_MESSAGE = "RARIBLE_POPUP_SELF_CLOSE"

/** How long a popup gets to close itself before our page closes it. */
const FALLBACK_CLOSE_MS = 1500

export interface PopupWindow {
  readonly closed: boolean
  close(): void
  postMessage(message: unknown, targetOrigin: string): void
}

export interface SelfCloseEvent {
  source: unknown
  origin: string
  data: unknown
}

export interface HostWindow {
  navigator: { userAgent: string; maxTouchPoints: number }
  location: { origin: string }
  opener: unknown
  open(
    url?: string | URL,
    target?: string,
    features?: string,
  ): PopupWindow | null
  close(): void
  addEventListener(
    type: "message",
    listener: (event: SelfCloseEvent) => void,
  ): void
}

type Schedule = (run: () => void, delayMs: number) => void

const installedHosts = new WeakSet<HostWindow>()

export function isIosWebKit(navigator: HostWindow["navigator"]) {
  if (/iPhone|iPad|iPod/.test(navigator.userAgent)) return true
  // iPadOS Safari requests the desktop site and reports itself as a Mac.
  return /Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1
}

export function deferPopupClose<T extends PopupWindow>(
  popup: T,
  origin: string,
  schedule: Schedule = (run, delayMs) => {
    setTimeout(run, delayMs)
  },
): T {
  let closeRequested = false
  const close = () => {
    if (closeRequested || popup.closed) return
    closeRequested = true
    // Dropped by the browser when the popup is on another origin.
    popup.postMessage({ type: POPUP_SELF_CLOSE_MESSAGE }, origin)
    schedule(() => {
      if (!popup.closed) popup.close()
    }, FALLBACK_CLOSE_MS)
  }

  // Reflect without a receiver so window getters and setters (`location`,
  // `closed`) run against the real window, not the proxy.
  return new Proxy(popup, {
    get(target, property) {
      if (property === "close") return close
      const value = Reflect.get(target, property)
      return typeof value === "function" ? value.bind(target) : value
    },
    set(target, property, value) {
      return Reflect.set(target, property, value)
    },
  })
}

function isSelfCloseMessage(data: unknown) {
  return (
    typeof data === "object" &&
    data !== null &&
    Reflect.get(data, "type") === POPUP_SELF_CLOSE_MESSAGE
  )
}

export function installIosPopupSelfClose(host: HostWindow) {
  if (installedHosts.has(host) || !isIosWebKit(host.navigator)) return
  installedHosts.add(host)

  // Privy opens a blank popup and navigates it afterwards, so it can open it
  // inside the click. Popups we open with a URL keep the native close().
  const open = host.open.bind(host)
  host.open = (url, target, features) => {
    const popup = open(url, target, features)
    if (!popup || url !== undefined) return popup
    return deferPopupClose(popup, host.location.origin)
  }

  host.addEventListener("message", (event) => {
    if (
      host.opener !== null &&
      event.source === host.opener &&
      event.origin === host.location.origin &&
      isSelfCloseMessage(event.data)
    ) {
      host.close()
    }
  })
}
